import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Typography } from "@mui/material";
import type { KeyboardEvent } from "react";
import { useState } from "react";

import { GetCallPlanDetailQuery } from "@/graphql/graphql";

import {
  activityDialogAuthorSx,
  activityDialogDescriptionBoxPlanDetailSx,
  activityDialogDescriptionTextSx,
  activityDialogParagraphSx,
} from "../CreateCallPlanModal/ActivityDialog/ActivityDialog.styles";
import QuestionBody from "../CreateCallPlanModal/ActivityDialog/QuestionBody";
import { formatEnumLabel } from "../CreateCallPlanModal/CreateCallPlanModal.utils";
import {
  viewCallPlanActivityChevronIconSx,
  viewCallPlanActivityChevronWrapSx,
  viewCallPlanActivityHeaderSx,
  viewCallPlanActivityHeaderTextColumnSx,
  viewCallPlanActivitySectionLabelSx,
  viewCallPlanActivityTitleSx,
  viewCallPlanInteractionSectionSx,
  viewCallPlanInteractionTitleSx,
} from "./ViewCallPlanModal.styles";

export type CallPlanDetailInteraction = NonNullable<
  NonNullable<GetCallPlanDetailQuery["callPlan"]["interactions"]>[number]
>;

export type ActivityInteractionDetail = Extract<
  CallPlanDetailInteraction,
  { __typename?: "ActivityInteraction" }
>;

const activitySectionToggleKeyActivate =
  (callbackFn: () => void) => (e: KeyboardEvent<Element>) => {
    if (e.key !== "Enter" && e.key !== " ") {
      return;
    }

    e.preventDefault();
    callbackFn();
  };

export const ActivitySection = ({ interaction }: { interaction: ActivityInteractionDetail }) => {
  const [activityExpanded, setActivityExpanded] = useState(false);

  const recordedBy = interaction.voiceMessage?.recordedBy;
  const authorLine =
    recordedBy &&
    [recordedBy.firstName, recordedBy.lastName]
      .filter((part) => part?.trim())
      .join(" ")
      .trim();

  const description = interaction.description;
  const showDescription = Boolean(description?.trim());

  const toggleActivityExpanded = () => setActivityExpanded((v) => !v);

  return (
    <Box sx={viewCallPlanInteractionSectionSx}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={activityExpanded}
        aria-label={
          activityExpanded ? "Collapse activity details" : `Expand activity: ${interaction.title}`
        }
        onClick={toggleActivityExpanded}
        onKeyDown={activitySectionToggleKeyActivate(toggleActivityExpanded)}
        sx={viewCallPlanActivityHeaderSx(activityExpanded)}
      >
        <Box component="span" sx={viewCallPlanActivityChevronWrapSx} aria-hidden>
          {activityExpanded ? (
            <KeyboardArrowUpIcon sx={viewCallPlanActivityChevronIconSx} />
          ) : (
            <KeyboardArrowRightIcon sx={viewCallPlanActivityChevronIconSx} />
          )}
        </Box>
        <Box sx={viewCallPlanActivityHeaderTextColumnSx}>
          <Typography component="span" sx={viewCallPlanActivitySectionLabelSx}>
            Activity
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={viewCallPlanActivityTitleSx(activityExpanded)}
          >
            {interaction.title}
          </Typography>
          {authorLine && (
            <Typography
              component="p"
              variant="body2"
              sx={activityDialogAuthorSx}
              aria-label={`Activity author: ${authorLine}`}
            >
              {authorLine}
            </Typography>
          )}
        </Box>
      </Box>
      {activityExpanded && (
        <>
          {showDescription && (
            <Box sx={activityDialogDescriptionBoxPlanDetailSx}>
              <Typography component="p" sx={activityDialogDescriptionTextSx}>
                {description}
              </Typography>
            </Box>
          )}
          <QuestionBody steps={interaction.contents} layout="planDetail" />
        </>
      )}
    </Box>
  );
};

export const InteractionSection = ({ interaction }: { interaction: CallPlanDetailInteraction }) => {
  if (interaction.__typename === "ActivityInteraction") {
    return <ActivitySection interaction={interaction} />;
  }
  // Will never happen, here to make TS happy
  if (interaction.__typename !== "OtherInteraction") {
    return null;
  }

  const body = interaction.contentText?.trim();

  return (
    <Box sx={viewCallPlanInteractionSectionSx}>
      <Typography component="h3" sx={viewCallPlanInteractionTitleSx}>
        {formatEnumLabel(interaction.type)}
      </Typography>
      {body ? (
        <Typography component="p" sx={activityDialogParagraphSx}>
          {body}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No instruction text for this step.
        </Typography>
      )}
    </Box>
  );
};
